import { AccessIcon, EmptyState, LazyLoader } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { Page } from '../../../../components/Page/Page';
import { TeachingBlockConflictProvider } from '../../../../components/TeachingBlockGrid/Contexts/TeachingBlockConflictContext';
import { TeachingBlockProvider } from '../../../../components/TeachingBlockGrid/Contexts/TeachingBlockContext';
import { TeachingBlockVersionTopBar } from '../../../../components/TeachingBlockVersion/TeachingBlockVersionTopBar/TeachingBlockVersionTopBar';
import { useTeachingBlockForVersion } from '../../../../components/Teachingblock/hooks/useTeachingBlocks';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { TeachingBlockDayAvailabilityStore } from '../../../../stores/DayAvailabilityStore';
import { StoreHandler } from '../../../../stores/StoreHandler';
import { getTeachingBlockAvailabilityRange } from '../../../../stores/TeachingBlockVersion/getTeachingBlockAvailabilityRange';
import {
  useDuplicateTeachingBlockVersionMutation,
  useSetTeachingBlockVersionEditableMutation,
} from '../../../../types/planung-graphql-client-defs';
import { blockIfEventJobRunning, createEventsForDateRange } from '../../../../utils/eventsApi';
import { BlockPhase } from '../TimetableVersion/TimetableVersionLayout';

export const availabilityStoreHandler = new StoreHandler<TeachingBlockDayAvailabilityStore>();

export const TeachingBlockVersion = observer(({ baseSlug }: { baseSlug: string }) => {
  const { t } = useTranslation();
  const { teachingBlockVersionUuid } = useParams();
  const permissions = usePermissionChecker();

  const auth = useAuthClaims();
  const organizationUuid = auth.pimAuthClaims.getOrganizationUuid();

  const selectedSchoolYear = useSelectedSchoolYear();

  const [, setVersionEditable] = useSetTeachingBlockVersionEditableMutation();
  const [, duplicateVersion] = useDuplicateTeachingBlockVersionMutation();

  const versionContext = useMemo(() => ({ additionalTypenames: ['TeachingBlockVersion'] }), []);
  const duplicateVersionContext = useMemo(
    () => ({ additionalTypenames: ['TeachingBlockVersion', 'TeachingBlockCard'] }),
    [],
  );

  const [inSchoolYear, setInSchoolYear] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);

  const { teachingBlock } = useTeachingBlockForVersion(teachingBlockVersionUuid);

  useEffect(() => {
    if (teachingBlockVersionUuid && selectedSchoolYear) {
      availabilityStoreHandler.register(teachingBlockVersionUuid, () => {
        const { from, to } = getTeachingBlockAvailabilityRange(selectedSchoolYear);
        return new TeachingBlockDayAvailabilityStore(dayjs(from).toDate(), dayjs(to).toDate());
      });
      setReady(true);
    }
  }, [selectedSchoolYear, teachingBlockVersionUuid]);

  useEffect(() => {
    if (teachingBlockVersionUuid) {
      const inSchoolYear = teachingBlock?.schoolYearUuid === selectedSchoolYear?.uuid;
      setInSchoolYear(inSchoolYear);
    }
  }, [teachingBlockVersionUuid, teachingBlock, selectedSchoolYear]);

  const isSnapshot = useMemo(() => {
    return teachingBlock?.versions.find((v) => v.uuid === teachingBlockVersionUuid)?.isSnapshot ?? false;
  }, [teachingBlock, teachingBlockVersionUuid]);

  // "Phase" der Epoche analog zum Stundenplan, abgeleitet aus dem Schuljahr
  // (= Zeitraum der Epoche): vor Schuljahresbeginn null (Version frei
  // bearbeitbar → kein Korrektur-Button), danach 'running'/'past'.
  const blockPhase = useMemo((): BlockPhase => {
    if (!selectedSchoolYear) return null;
    const today = dayjs();
    if (today.isBefore(dayjs(selectedSchoolYear.start), 'day')) return null;
    if (today.isAfter(dayjs(selectedSchoolYear.end), 'day')) return 'past';
    return 'running';
  }, [selectedSchoolYear]);

  async function handleStartFix() {
    if (!teachingBlockVersionUuid) return;
    await setVersionEditable({ uuid: teachingBlockVersionUuid, editable: true }, versionContext);
    await duplicateVersion(
      {
        organizationUuid,
        teachingBlockVersionUuid,
        options: { snapshot: true },
      },
      duplicateVersionContext,
    );
  }

  // Gibt `true` zurück, wenn die Aktion tatsächlich durchlief – nur dann darf die
  // TopBar den temporären Edit-Status zurücksetzen.
  async function handleCompleteFix(): Promise<boolean> {
    if (!teachingBlockVersionUuid) return false;
    if (await blockIfEventJobRunning()) return false;
    await setVersionEditable({ uuid: teachingBlockVersionUuid, editable: false }, versionContext);
    if (selectedSchoolYear) {
      await createEventsForDateRange({
        organizationUuid,
        schoolYearUuid: selectedSchoolYear.uuid,
        from: dayjs().format('YYYY-MM-DD'),
        to: dayjs(selectedSchoolYear.end).format('YYYY-MM-DD'),
        epochSubjectContainerUuid: teachingBlock?.subjectContainerUuid,
      });
    }
    return true;
  }

  return (
    <Page isForbidden={!permissions?.canReadTimetable()} fullHeight fullWidth>
      {teachingBlockVersionUuid && ready && (
        <>
          {inSchoolYear ? (
            <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='60vh' />}>
              <TeachingBlockConflictProvider>
                <TeachingBlockProvider>
                  <TeachingBlockVersionTopBar
                    versionUuid={teachingBlockVersionUuid}
                    baseSlug={baseSlug}
                    blockPhase={blockPhase}
                    onStartFix={isSnapshot ? undefined : handleStartFix}
                    onCompleteFix={isSnapshot ? undefined : handleCompleteFix}
                  />
                  <Outlet />
                </TeachingBlockProvider>
              </TeachingBlockConflictProvider>
            </Suspense>
          ) : (
            <EmptyState
              icon={<AccessIcon color='var(--color-error)' />}
              title={t('timetableVersion.notInSchoolYear')}
              forcedHeight='60vh'
            />
          )}
        </>
      )}
    </Page>
  );
});
